import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import "@/index.css";
import { AppProvider } from "@/context/AppContext";
import AppLayout from "@/components/AppLayout";
import PublicLayout from "@/components/PublicLayout";
import Chat from "@/pages/Chat";
import Memory from "@/pages/Memory";
import Reminders from "@/pages/Reminders";
import Settings from "@/pages/Settings";
import Search from "@/pages/Search";
import Tasks from "@/pages/Tasks";
import Contacts from "@/pages/Contacts";
import Home from "@/pages/public/Home";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import Messaging from "@/pages/public/Messaging";
import AccessGate from "@/components/AccessGate";
import { pruneDrafts } from "@/hooks/useDraft";

/**
 * V9.52 — Removed the public-only-host gate. Previously
 * `journeywithmonte.com` was locked to marketing-only content and the
 * assistant lived on a separate `.emergent.host` URL, which caused
 * PWA-installed-from-wrong-host data forks. Every host now runs the
 * full app; `/site`, `/privacy`, `/terms`, `/messaging` remain
 * available as static marketing pages under `PublicLayout`.
 */

function GatedAppShell() {
  // Nested `<Routes>` inside the outer `<Route path="/app/*">` matches
  // against the REMAINING url only (react-router v6/v7 semantics). Paths
  // here MUST be relative — `path="/"` matches `/app`, `path="memory"`
  // matches `/app/memory`, etc.
  return (
    <AppProvider>
      <Routes>
        <Route path="/" element={<AppLayout />}>
          <Route index element={<Chat />} />
          <Route path="memory" element={<Memory />} />
          <Route path="reminders" element={<Reminders />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="search" element={<Search />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        {/* Any /app/* URL not matched above falls back to the chat home. */}
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </AppProvider>
  );
}

function InternalApp() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public marketing + compliance pages — no AccessGate.
            `/` is the marketing home; Twilio compliance reviewers land
            here and the SMS-consent workflow lives at /messaging. */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/messaging" element={<Messaging />} />
          {/* Legacy alias — `/site` used to be the marketing home on
              the private host. Kept as a permanent redirect so any
              old bookmarks still resolve. */}
          <Route path="/site" element={<Navigate to="/" replace />} />
        </Route>

        {/* Full Journey assistant — path-scoped under /app. AccessGate
            protects every route inside. PWA is installed from /app so
            tapping the icon opens the assistant directly. */}
        <Route
          path="/app/*"
          element={
            <AccessGate>
              <GatedAppShell />
            </AccessGate>
          }
        />

        {/* Any unknown path outside of /app/* falls back to marketing. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  // Sweep stale (>7d) and malformed drafts on app boot.
  useEffect(() => {
    pruneDrafts();
  }, []);

  return (
    <div className="App" data-host-mode="internal">
      <InternalApp />
    </div>
  );
}

export default App;
